// src/components/NewInvoice/InvoiceItemForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import Select from "react-select";
import { motion, AnimatePresence } from "framer-motion";
import { FaTrash } from "react-icons/fa";
import { useGetAllLabelledStockQuery } from "../../toolkitServices/stockApi";
import { useDebounce } from "../../hooks/useDebounced";
import { handleDropDowns } from "../../config/dropDownHelpers";
import { useSelector } from "react-redux";
import { threeDecimal, twoDecimal } from "../../config/helperFunctions";
import { gst } from "../../config/taxes";
import { BsQrCodeScan } from "react-icons/bs";

import { GiCutDiamond } from "react-icons/gi";
import InvoiceStonePopUp from "./InvoiceStonePopUp";
import InvoiceDiamondPopUp from "./InvoiceDiamondPopUp";
import { createPortal } from "react-dom";
import { useGetAllEmployeesQuery } from "../../toolkitServices/employeeApi";
import { useGetModuleFieldsQuery } from "../../toolkitServices/moduleFieldsApi";
import QrScannerModal from "../QrScannerModal";
import CreatableSelect from "react-select/creatable";

// tiny toast helper
const toast = (msg, type = "error") => {
  if (window?.toastr?.[type]) return window.toastr[type](msg);
  if (window?.toast?.[type]) return window.toast[type](msg);
  if (window?.notify) return window.notify(msg, { type });
  alert(msg);
};

export const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    borderColor: state.isFocused ? "#A78BFA" : "#D1D5DB",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(167,139,250,0.35)" : "none",
    fontSize: "12px",
    backgroundColor: "white",
  }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "1px",
    svg: {
      width: "10px",
      height: "10px",
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "1px",
    svg: {
      width: "10px",
      height: "10px",
    },
  }),
  indicatorSeparator: () => ({
    display: "none", // 👈 beech ki line hata di
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", height: "20px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  menu: (base) => ({ ...base, fontSize: "12px" }),
  menuList: (base) => ({ ...base, maxHeight: "100px", overflowY: "auto" }),
  singleValue: (base) => ({
    ...base,
    fontSize: "11px", // 👈 SELECTED TEXT SIZE
    lineHeight: "1.1",
    margin: 0,
    padding: 0,
    whiteSpace: "nowrap", // 👈 ek line
    overflow: "hidden", // 👈 cut ho jaaye
    textOverflow: "ellipsis", // 👈 ...
  }),
};

const withErrorBorder = (stylesObj, hasError) => {
  if (!hasError) return stylesObj;
  return {
    ...stylesObj,
    control: (base, state) => {
      const baseCtrl = stylesObj.control
        ? stylesObj.control(base, state)
        : base;
      return {
        ...baseCtrl,
        borderColor: "#ef4444",
        boxShadow: state.isFocused ? "0 0 0 2px rgba(239,68,68,0.20)" : "none",
        "&:hover": { borderColor: "#ef4444" },
      };
    },
  };
};

const inputCls = (hasErr) =>
  `w-full px-3 py-[4.5px] text-sm border rounded-[4px] bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 ${
    hasErr
      ? "border-red-400 focus:ring-red-300 focus:border-red-400"
      : "border-gray-300 focus:ring-violet-300 focus:border-violet-300"
  }`;

// robust id picker for rates (string/object safe)
const pickId = (v) => {
  if (!v) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  return String(v?._id ?? v?.id ?? v?.value ?? "");
};

const ADJUSTMENT_MODE = {
  DISCOUNT: "DISCOUNT",
  MAKING: "MAKING",
  MAKINGPERGRAM: "MAKINGPERGRAM",
  FIXEDAMOUNT: "FIXEDAMOUNT",
  FIXEDWASTAGE: "FIXEDWASTAGE",
};

const round2 = (v) => +Number(v || 0).toFixed(2);
const round3 = (v) => +Number(v || 0).toFixed(3);
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const getRowAdjustmentSnapshot = ({ getValues, rowindex }) => {
  const entry = (f) => `stockEntries.${rowindex}.${f}`;

  const makingPercentage = num(getValues(entry("makingPercentage")));
  const makingPerGram = num(getValues(entry("makingPerGram")));
  const fixedWastage = num(getValues(entry("fixedWastage")));
  const fixedAmount = num(getValues(entry("fixedAmount")));
  const netWt = num(getValues(entry("netWt")));
  const ratePerGram = num(getValues(entry("ratePerGram")));

  const makingPctAmount = round2(
    (netWt * ratePerGram * makingPercentage) / 100,
  );
  const makingPerGramAmount = round2(netWt * makingPerGram);
  const fixedWastageAmount = round2(ratePerGram * fixedWastage);
  const fixedAmountAmount = round2(fixedAmount);

  const activeModes = [];
  if (makingPercentage > 0) activeModes.push(ADJUSTMENT_MODE.MAKING);
  if (makingPerGram > 0) activeModes.push(ADJUSTMENT_MODE.MAKINGPERGRAM);
  if (fixedWastage > 0) activeModes.push(ADJUSTMENT_MODE.FIXEDWASTAGE);
  if (fixedAmount > 0) activeModes.push(ADJUSTMENT_MODE.FIXEDAMOUNT);

  return {
    makingPercentage,
    makingPerGram,
    fixedWastage,
    fixedAmount,
    netWt,
    ratePerGram,
    makingPctAmount,
    makingPerGramAmount,
    fixedWastageAmount,
    fixedAmountAmount,
    activeModes,
  };
};

const resolveRowAdjustmentTarget = ({
  getValues,
  rowindex,
  manualAdjustmentMode,
  direction,
}) => {
  const snap = getRowAdjustmentSnapshot({ getValues, rowindex });
  const activeModes = snap.activeModes || [];

  if (activeModes.length === 0) {
    return direction === "increase"
      ? ADJUSTMENT_MODE.FIXEDAMOUNT
      : ADJUSTMENT_MODE.DISCOUNT;
  }

  if (activeModes.length === 1) {
    return activeModes[0];
  }

  if (activeModes.includes(manualAdjustmentMode)) {
    return manualAdjustmentMode;
  }

  return direction === "increase"
    ? ADJUSTMENT_MODE.FIXEDAMOUNT
    : ADJUSTMENT_MODE.DISCOUNT;
};

const applyIncreaseOnRow = ({
  getValues,
  setValue,
  rowindex,
  mode,
  addPretax,
}) => {
  const entry = (f) => `stockEntries.${rowindex}.${f}`;
  const snap = getRowAdjustmentSnapshot({ getValues, rowindex });

  if (mode === ADJUSTMENT_MODE.MAKING) {
    const base = round2(snap.netWt * snap.ratePerGram);

    if (base <= 0) {
      const currentFixed = num(getValues(entry("fixedAmount")));
      setValue(entry("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addPct = round2((addPretax / base) * 100);
    setValue(
      entry("makingPercentage"),
      round2(snap.makingPercentage + addPct),
      { shouldDirty: true },
    );
    return;
  }

  if (mode === ADJUSTMENT_MODE.MAKINGPERGRAM) {
    if (snap.netWt <= 0) {
      const currentFixed = num(getValues(entry("fixedAmount")));
      setValue(entry("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addPerGram = round2(addPretax / snap.netWt);
    setValue(entry("makingPerGram"), round2(snap.makingPerGram + addPerGram), {
      shouldDirty: true,
    });
    return;
  }

  if (mode === ADJUSTMENT_MODE.FIXEDWASTAGE) {
    if (snap.ratePerGram <= 0) {
      const currentFixed = num(getValues(entry("fixedAmount")));
      setValue(entry("fixedAmount"), round2(currentFixed + addPretax), {
        shouldDirty: true,
      });
      return;
    }

    const addWt = round3(addPretax / snap.ratePerGram);
    setValue(entry("fixedWastage"), round3(snap.fixedWastage + addWt), {
      shouldDirty: true,
    });
    return;
  }

  const currentFixedAmount = num(getValues(entry("fixedAmount")));
  setValue(entry("fixedAmount"), round2(currentFixedAmount + addPretax), {
    shouldDirty: true,
  });
};


const applyReductionOnRow = ({
  getValues,
  setValue,
  rowindex,
  mode,
  reducePretax,
}) => {
  const entry = (f) => `stockEntries.${rowindex}.${f}`;
  const snap = getRowAdjustmentSnapshot({ getValues, rowindex });

  if (mode === ADJUSTMENT_MODE.DISCOUNT) {
    return round2(reducePretax);
  }

  if (mode === ADJUSTMENT_MODE.MAKING) {
    const consumable = Math.min(snap.makingPctAmount, reducePretax);
    const base = round2(snap.netWt * snap.ratePerGram);

    if (base > 0) {
      const newAmount = round2(snap.makingPctAmount - consumable);
      const newPct = round2((newAmount / base) * 100);
      setValue(entry("makingPercentage"), newPct, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  if (mode === ADJUSTMENT_MODE.MAKINGPERGRAM) {
    const consumable = Math.min(snap.makingPerGramAmount, reducePretax);

    if (snap.netWt > 0) {
      const newAmount = round2(snap.makingPerGramAmount - consumable);
      const newPerGram = round2(newAmount / snap.netWt);
      setValue(entry("makingPerGram"), newPerGram, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  if (mode === ADJUSTMENT_MODE.FIXEDWASTAGE) {
    const consumable = Math.min(snap.fixedWastageAmount, reducePretax);

    if (snap.ratePerGram > 0) {
      const newAmount = round2(snap.fixedWastageAmount - consumable);
      const newWt = round3(newAmount / snap.ratePerGram);
      setValue(entry("fixedWastage"), newWt, { shouldDirty: true });
    }

    return round2(reducePretax - consumable);
  }

  // FIXEDAMOUNT
  const consumable = Math.min(snap.fixedAmountAmount, reducePretax);
  const newFixed = round2(snap.fixedAmount - consumable);
  setValue(entry("fixedAmount"), newFixed, { shouldDirty: true });

  return round2(reducePretax - consumable);
};

const InvoiceItemForm = ({
  control,
  register,
  unregister, // (unused now—kept in signature for API parity)
  errors,
  rowindex,
  fieldId,
  onDelete,
  watch,
  setValue,
  getValues,
  showDelete = true,
  cardHeightClass = "h-[40vh] sm:h-[40vh]",
  registerFirstField,
  selectedBranch,
  mode,
  manualAdjustmentMode = "DISCOUNT",
}) => {
  const formRef = useRef(null);
  const stoneBtnRef = useRef(null);
  const diamondBtnRef = useRef(null);

  const page = 1;

  const [searchItemCode, setSearchItemCode] = useState("");
  const [openStoneModal, setOpenStoneModal] = useState(false);
  const [openDiamondModal, setOpenDiamondModal] = useState(false);

  const [openQr, setOpenQr] = useState(false);
  const [pendingQrCode, setPendingQrCode] = useState(null);

  const search = useDebounce(searchItemCode, 800);

  const {
    data: { data: invoiceFields = [] } = {},
    isLoading: invoiceFieldsLoading,
  } = useGetModuleFieldsQuery("INVOICE_FIELDS");

  // ✅ use server fields; keep only visible
  const PakkaInvoiceFields = useMemo(
    () => invoiceFields.filter((f) => f.visibility !== false),
    [invoiceFields],
  );
  console.log(PakkaInvoiceFields);

  const { data: employees } = useGetAllEmployeesQuery({
    page: 1,
    limit: 2000,
    search: "",
    department: "",
    branch: selectedBranch || undefined,
    active: true,
  });

  const categoriesFromState = useSelector((s) => s.categories?.categories);
  const productsFromState = useSelector((s) => s.products?.products);
  const designFromState = useSelector((s) => s.designs?.designs);
  const purityFromState = useSelector((s) => s.purities?.purities);
  const branchesFromState = useSelector((s) => s.branches?.branches);
  const countersFromState = useSelector((s) => s.counters?.counters);
  const lastestRates = useSelector((s) => s.lastestRates);

  const category = watch(`stockEntries.${rowindex}.category`);
  const product = watch(`stockEntries.${rowindex}.product`);
  const purity = watch(`stockEntries.${rowindex}.purity`);
  const itemCodeDetail = watch(`stockEntries.${rowindex}.itemCodeDetail`);
  const itemCode = watch(`stockEntries.${rowindex}.itemCode`);

  // live watch
  const stones =
    useWatch({ control, name: `stockEntries.${rowindex}.stones` }) || [];
  const diamonds =
    useWatch({ control, name: `stockEntries.${rowindex}.diamonds` }) || [];

  // per-row counter
  const counterSel = useWatch({
    control,
    name: `stockEntries.${rowindex}.counterRow`,
  });

  const counterId = counterSel?.value || "";
  const branchIdFromCounter = counterSel?.info?.branchId || "";

  // avoid clearing item code when counter is set by itemCode selection
  const skipClearOnNextCounterChangeRef = useRef(false);
  const prevCounterIdRef = useRef(counterId);

  // ✅ rate watch (programmatic setValue + manual both)
  const ratePerGramWatch = useWatch({
    control,
    name: `stockEntries.${rowindex}.ratePerGram`,
  });

  // ✅ QR scan pe thoda zyada list mangao taaki find ho sake
  const effectiveLimit = pendingQrCode ? 50 : 5;

  const { data } = useGetAllLabelledStockQuery({
    page,
    limit: effectiveLimit,
    search,
    branchId: selectedBranch?.value || "",
  });

  const initialStones = stones;
  const initialDiamonds = diamonds;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showDelete) return;
      if (e.ctrlKey && e.key === "Delete") {
        if (
          formRef.current &&
          formRef.current.contains(document.activeElement)
        ) {
          e.preventDefault();
          onDelete?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onDelete, showDelete]);

  // when counter changes → snapshot primitives; only clear item code if actual counter changed
  useEffect(() => {
    const prevCounterId = prevCounterIdRef.current;
    const currentCounterId = counterSel?.value || "";

    setValue(`stockEntries.${rowindex}.counterId`, currentCounterId, {
      shouldDirty: true,
    });
    setValue(`stockEntries.${rowindex}.counterName`, counterSel?.label || "", {
      shouldDirty: true,
    });
    setValue(`stockEntries.${rowindex}.branchId`, branchIdFromCounter || "", {
      shouldDirty: true,
    });
    setValue(
      `stockEntries.${rowindex}.branchName`,
      counterSel?.info?.branchName || "",
      { shouldDirty: true },
    );

    const counterActuallyChanged = prevCounterId !== currentCounterId;

    if (counterActuallyChanged) {
      if (skipClearOnNextCounterChangeRef.current) {
        skipClearOnNextCounterChangeRef.current = false;
      } else {
        setValue(`stockEntries.${rowindex}.itemCodeDetail`, null, {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.itemCode`, null, {
          shouldDirty: true,
        });

        // clear stones/diamonds when counter truly changed (keep if desired UX)
        setValue(`stockEntries.${rowindex}.stones`, [], { shouldDirty: true });
        setValue(`stockEntries.${rowindex}.stonePcs`, "0", {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.stoneAmount`, "0", {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.stoneWt`, "0", {
          shouldDirty: true,
        });

        setValue(`stockEntries.${rowindex}.diamonds`, [], {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.diamondPcs`, "0", {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.diamondAmount`, "0", {
          shouldDirty: true,
        });
        setValue(`stockEntries.${rowindex}.diamondWt`, "0", {
          shouldDirty: true,
        });
      }
    }

    prevCounterIdRef.current = currentCounterId;
  }, [counterSel, branchIdFromCounter, rowindex, setValue]);

  useEffect(() => {
    if (mode != "edit") {
      if (category?.value && purity?.value) {
        let found = lastestRates?.rates?.find(
          (item) =>
            item?.categoryId?._id == category?.value &&
            item?.purityId?._id == purity?.value,
        );
        setValue(`stockEntries.${rowindex}.ratePerGram`, found?.ratePerGram);
      } else {
        setValue(`stockEntries.${rowindex}.ratePerGram`, 0);
      }
    }
  }, [category, purity, lastestRates]);

  const allEntries = useWatch({ control, name: "stockEntries" }) || [];
  const usedItemCodes = useMemo(() => {
    return new Set(
      allEntries
        .map((e, idx) => (idx === rowindex ? null : e?.itemCode?.value))
        .filter(Boolean),
    );
  }, [allEntries, rowindex]);

  const handleCalculations = ({ rowindex, getValues, setValue }) => {
    const entry = (f) => `stockEntries.${rowindex}.${f}`;
    const get = (key) => Number(getValues(entry(key)) || 0);

    const totalWt = get("totalWt");
    const packingWt = get("packingWt");
    const stoneWt = get("stoneWt");
    const diamondCt = get("diamondWt"); // store **carat**
    const ratePerGram = get("ratePerGram");
    const makingPct = get("makingPercentage");
    const makingPerGram = get("makingPerGram");
    const fixedMaking = get("fixedWastage");
    const fixedAmount = get("fixedAmount");
    const stoneAmount = get("stoneAmount");
    const diamondAmount = get("diamondAmount");
    const hallmarkAmount = get("hallmarkAmount");
    const itemDisc = get("itemDiscountAmount");

    const grossWt = +(totalWt - packingWt).toFixed(3);
    setValue(entry("grossWt"), grossWt, { shouldDirty: true });

    // diamondCt is carat → grams = ct * 0.2
    const diamondGram = +(diamondCt * 0.2).toFixed(3);

    const netWt = +(grossWt - stoneWt - diamondGram).toFixed(3);
    setValue(entry("netWt"), netWt, { shouldDirty: true });

    const makingBaseWt = netWt;
    const amtByFixedMaking = +(fixedMaking * ratePerGram).toFixed(2);
    const amtByMakingPerGram = +(makingPerGram * netWt).toFixed(2);
    const amtByPercentage = +(
      (makingBaseWt * ratePerGram * makingPct) /
      100
    ).toFixed(2);

    const metalAmount = +(ratePerGram * netWt).toFixed(2);
    const preTaxMaking = +(
      amtByMakingPerGram +
      amtByPercentage +
      amtByFixedMaking +
      fixedAmount
    ).toFixed(2);

    const itemAmount = +(
      metalAmount +
      preTaxMaking +
      stoneAmount +
      diamondAmount +
      hallmarkAmount
    ).toFixed(2);

    const mrpVal = Number(getValues(entry("mrp")) || 0);

    if (mrpVal > 0) {
      const gstRate = gst;

      // 1️⃣ Back calculate original taxable
      const taxable = +(mrpVal / (1 + gstRate / 100)).toFixed(2);

      // keep original taxable
      setValue(entry("itemAmount"), taxable);

      const discount = Number(getValues(entry("itemDiscountAmount")) || 0);
      const taxableAfterDiscount = +(taxable - discount).toFixed(2);

      const newGst = +((taxableAfterDiscount * gstRate) / 100).toFixed(2);
      const finalAmount = +(taxableAfterDiscount + newGst).toFixed(2);

      setValue(entry("itemAmountAfterDiscount"), taxableAfterDiscount);
      setValue(entry("itemGstAmount"), newGst);
      setValue(entry("totalItemAmount"), finalAmount);

      return;
    }
    // ORIGINAL BASE AMOUNT
    setValue(entry("itemAmount"), itemAmount, { shouldDirty: true });

    // AFTER DISCOUNT TAXABLE
    const taxableAfterDiscount = +(itemAmount - itemDisc).toFixed(2);
    setValue(entry("itemAmountAfterDiscount"), taxableAfterDiscount, {
      shouldDirty: true,
    });

    // GST on AFTER discount
    const itemGstAmount = +((taxableAfterDiscount * gst) / 100).toFixed(2);
    setValue(entry("itemGstAmount"), itemGstAmount, { shouldDirty: true });

    // Final total
    const totalItemAmount = +(taxableAfterDiscount + itemGstAmount).toFixed(2);
    setValue(entry("totalItemAmount"), totalItemAmount, { shouldDirty: true });

    return {
      itemAmount,
      itemGstAmount,
      totalItemAmount,
    };
  };

  const applyManualFinalAmount = (rowindex, targetTotal) => {
  const entry = (f) => `stockEntries.${rowindex}.${f}`;
  const mrpVal = Number(getValues(entry("mrp")) || 0);

  if (mrpVal > 0) {
    const gstRate = gst;

    const taxable = +(mrpVal / (1 + gstRate / 100)).toFixed(2);
    const discount = Number(getValues(entry("itemDiscountAmount")) || 0);
    const taxableAfterDiscount = +(taxable - discount).toFixed(2);
    const newGst = +((taxableAfterDiscount * gstRate) / 100).toFixed(2);
    const finalAmount = +(taxableAfterDiscount + newGst).toFixed(2);

    setValue(entry("itemAmount"), taxable);
    setValue(entry("itemAmountAfterDiscount"), taxableAfterDiscount);
    setValue(entry("itemGstAmount"), newGst);
    setValue(entry("totalItemAmount"), finalAmount);
    return;
  }

  const auto = handleCalculations({ rowindex, getValues, setValue });
  const autoTotal = +(auto.totalItemAmount || 0).toFixed(2);

  if (!Number.isFinite(targetTotal) || targetTotal <= 0) {
    setValue(entry("totalItemAmount"), autoTotal, { shouldDirty: true });
    return;
  }

  // SAME TOTAL
  if (targetTotal <= autoTotal + 0.001 && targetTotal >= autoTotal - 0.001) {
    setValue(entry("totalItemAmount"), autoTotal, { shouldDirty: true });
    return;
  }

  // =========================
  // INCREASE
  // =========================
  if (targetTotal > autoTotal + 0.001) {
    const deltaTotal = +(targetTotal - autoTotal).toFixed(2);
    const addPretax = +(deltaTotal / (1 + gst / 100)).toFixed(2);

    const targetMode = resolveRowAdjustmentTarget({
      getValues,
      rowindex,
      manualAdjustmentMode,
      direction: "increase",
    });

    applyIncreaseOnRow({
      getValues,
      setValue,
      rowindex,
      mode: targetMode,
      addPretax,
    });

    const after = handleCalculations({ rowindex, getValues, setValue });
    setValue(entry("totalItemAmount"), after.totalItemAmount, {
      shouldDirty: true,
    });
    return;
  }

  // =========================
  // DECREASE
  // =========================
  const deltaTotal = +(autoTotal - targetTotal).toFixed(2);
  const reducePretax = +(deltaTotal / (1 + gst / 100)).toFixed(2);

  const targetMode = resolveRowAdjustmentTarget({
    getValues,
    rowindex,
    manualAdjustmentMode,
    direction: "decrease",
  });

  const leftover = applyReductionOnRow({
    getValues,
    setValue,
    rowindex,
    mode: targetMode,
    reducePretax,
  });

  if (leftover > 0) {
    const currentItemDiscount = Number(getValues(entry("itemDiscountAmount")) || 0);
    setValue(
      entry("itemDiscountAmount"),
      +(currentItemDiscount + leftover).toFixed(2),
      { shouldDirty: true }
    );
  }

  const after = handleCalculations({ rowindex, getValues, setValue });
  setValue(entry("totalItemAmount"), after.totalItemAmount, {
    shouldDirty: true,
  });
};

  const handleItemCodeSelect = (opt) => {
    // ❌ duplicate item check
    if (opt && usedItemCodes.has(opt.value)) {
      toast("This item code is already added in another row.", "error");
      return;
    }

    // clear case
    if (!opt) {
      setValue(`stockEntries.${rowindex}.itemCode`, null);
      setValue(`stockEntries.${rowindex}.stones`, []);
      setValue(`stockEntries.${rowindex}.diamonds`, []);
      setValue(`stockEntries.${rowindex}.stonePcs`, "0");
      setValue(`stockEntries.${rowindex}.stoneWt`, "0");
      setValue(`stockEntries.${rowindex}.stoneAmount`, "0");
      setValue(`stockEntries.${rowindex}.diamondPcs`, "0");
      setValue(`stockEntries.${rowindex}.diamondWt`, "0");
      setValue(`stockEntries.${rowindex}.diamondAmount`, "0");
      handleCalculations({ rowindex, getValues, setValue });
      return;
    }

    const res = opt.info;

    // snapshot itemCode
    setValue(`stockEntries.${rowindex}.itemCode`, {
      value: opt.value,
      label: opt.label,
    });

    // counter auto-set (skip auto-clear)
    if (res?.counterId) {
      skipClearOnNextCounterChangeRef.current = true;
      setValue(`stockEntries.${rowindex}.counterRow`, {
        value: res.counterId,
        label: res.counterName,
      });
    }

    // category / product / design / purity
    setValue(`stockEntries.${rowindex}.category`, {
      value: res.categoryId,
      label: res.categoryName,
    });
    setValue(`stockEntries.${rowindex}.product`, {
      value: res.productId,
      label: res.productName,
    });
    setValue(`stockEntries.${rowindex}.design`, {
      value: res.designId,
      label: res.designName,
    });
    setValue(`stockEntries.${rowindex}.purity`, {
      value: res.purityId,
      label: res.purityName,
    });

    // weights
    setValue(`stockEntries.${rowindex}.totalWt`, res.totalWt);
    setValue(`stockEntries.${rowindex}.packingWt`, threeDecimal(res.packingWt));
    setValue(
      `stockEntries.${rowindex}.grossWt`,
      threeDecimal((res.totalWt || 0) - (res.packingWt || 0)),
    );

    setValue(`stockEntries.${rowindex}.stoneWt`, threeDecimal(res.stoneWt));
    setValue(`stockEntries.${rowindex}.diamondWt`, threeDecimal(res.diamondWt)); // carat

    setValue(
      `stockEntries.${rowindex}.netWt`,
      threeDecimal(
        (res.totalWt || 0) -
          (res.packingWt || 0) -
          (res.stoneWt || 0) -
          (+res.diamondWt || 0) * 0.2,
      ),
    );

    // qty / pcs
    setValue(`stockEntries.${rowindex}.qty`, res.qty);
    setValue(`stockEntries.${rowindex}.pieces`, res.pcs);
    setValue(`stockEntries.${rowindex}.stonePcs`, res.stonePcs);
    setValue(`stockEntries.${rowindex}.diamondPcs`, res.diamondPcs);

    // pricing (NOTE: ratePerGram comes from latestRates effect OR manual)
    setValue(`stockEntries.${rowindex}.makingPercentage`, res.makingPercentage);
    setValue(`stockEntries.${rowindex}.makingPerGram`, res.makingPerGram);
    setValue(`stockEntries.${rowindex}.fixedWastage`, res.fixedMaking);
    setValue(`stockEntries.${rowindex}.fixedAmount`, res.fixedAmount);
    setValue(`stockEntries.${rowindex}.stoneAmount`, res.totalStoneAmount);
    setValue(`stockEntries.${rowindex}.diamondAmount`, res.totalDiamondAmount);
    setValue(`stockEntries.${rowindex}.hallmarkAmount`, res.hallmarkCharge);
    setValue(`stockEntries.${rowindex}.huidCode`, res.huidCode);
    setValue(`stockEntries.${rowindex}.mrp`, res.mrp);

    // counter / branch snapshot
    setValue(`stockEntries.${rowindex}.counterId`, counterSel?.value || "");
    setValue(`stockEntries.${rowindex}.counterName`, counterSel?.label || "");
    setValue(`stockEntries.${rowindex}.branchId`, branchIdFromCounter || "");
    setValue(
      `stockEntries.${rowindex}.branchName`,
      counterSel?.info?.branchName || "",
    );

    // stones prefill
    const mappedStones = (res.stones || []).map((s) => ({
      stnPopupName: s.stoneId ? String(s.stoneId) : "",
      unit: s.unit || "gram",
      stnPopupWt: s.wt ?? "",
      stnPopupPcs: s.pcs ?? "",
      stnPopupAmt: s.amount ?? "",
      stnPopupDeduct: s.deduct || "",
      stnPopupRateByWt: s?.rateByWt || "",
    }));
    setValue(`stockEntries.${rowindex}.stones`, mappedStones);

    // diamonds prefill
    const mappedDiamonds = (res.diamonds || []).map((d) => ({
      diaPopupName: d.diamondId ? String(d.diamondId) : "",
      diaPopupWt: d.wt ?? "",
      diaPopupPcs: d.pcs ?? "",
      diaPopupAmt: d.amount ?? "",
      diaPopupDeduct: d.deduct || "",
    }));
    setValue(`stockEntries.${rowindex}.diamonds`, mappedDiamonds);

    // initial calc (ratePerGram may update a moment later from latestRates effect,
    // so we also recalc on rate change)
    handleCalculations({ rowindex, getValues, setValue });
  };

  // ✅ latest rates: programmatic setValue + manual trigger calc
  useEffect(() => {
    if (mode === "edit") return;

    const catId = category?.value;
    const purId = purity?.value;

    if (!catId || !purId) {
      setValue(`stockEntries.${rowindex}.ratePerGram`, 0, {
        shouldDirty: true,
        shouldValidate: true,
      });
      requestAnimationFrame(() => {
        handleCalculations({ rowindex, getValues, setValue });
      });
      return;
    }

    const rates = lastestRates?.rates || [];
    const found = rates.find((r) => {
      const rCat = pickId(r?.categoryId);
      const rPur = pickId(r?.purityId);
      return String(rCat) === String(catId) && String(rPur) === String(purId);
    });

    const rate = Number(found?.ratePerGram ?? 0);

    setValue(`stockEntries.${rowindex}.ratePerGram`, rate, {
      shouldDirty: true,
      shouldValidate: true,
    });

    requestAnimationFrame(() => {
      handleCalculations({ rowindex, getValues, setValue });
    });
  }, [
    category?.value,
    purity?.value,
    lastestRates,
    mode,
    rowindex,
    setValue,
    getValues,
  ]);

  // ✅ recalc whenever rate changes (covers BOTH: manual typing & programmatic setValue)
  useEffect(() => {
    // guard: avoid running too early if form not ready
    // if (!category?.value && !itemCodeDetail) return;
    handleCalculations({ rowindex, getValues, setValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratePerGramWatch]);

  // ✅ QR detected
  const handleQrDetected = (rawText) => {
    const code = rawText?.trim();
    if (!code) return;

    setSearchItemCode(code);
    setPendingQrCode(code);
  };

  // ✅ ONLY ONE effect: scan → find → select → close QR → focus dropdown
  useEffect(() => {
    if (!pendingQrCode) return;
    if (!data?.data) return;

    const found = data.data.find(
      (x) =>
        String(x.itemCode).toLowerCase() ===
        String(pendingQrCode).toLowerCase(),
    );

    // not found
    if (!found) {
      toast("Scanned item not found in labelled stock", "error");
      setPendingQrCode(null);

      requestAnimationFrame(() => {
        document
          .querySelector(`#invoice-card-${fieldId} .react-select__control`)
          ?.focus?.();
      });

      return;
    }

    const opt = {
      value: found._id,
      label: found.itemCode,
      info: found,
    };

    setValue(`stockEntries.${rowindex}.itemCodeDetail`, opt, {
      shouldDirty: true,
    });

    handleItemCodeSelect(opt);
    setOpenQr(false);
    setPendingQrCode(null);

    requestAnimationFrame(() => {
      document
        .querySelector(`#invoice-card-${fieldId} .react-select__control`)
        ?.focus?.();
    });
  }, [pendingQrCode, data, fieldId, rowindex, setValue]); // handleItemCodeSelect stays inside component

  return (
    <motion.div
      ref={formRef}
      tabIndex={-1}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: rowindex * 0.05 }}
      id={`invoice-card-${fieldId}`}
      data-field-id={fieldId}
      className={`invoice-card ${cardHeightClass} snap-start px-2 pt-1 pb-2 md:pb-0 rounded-lg border border-gray-200 hover:border-gray-300 shadow-lg bg-white relative overflow-y-auto`}
    >
      <div className="mb-0.5 flex items-center justify-between sticky top-0 bg-white pt-1 pb-1 z-10">
        <h3 className="text-sm font-semibold text-gray-900">
          #{rowindex + 1} • Invoice Item
        </h3>
        <div className="flex-1 h-px bg-gray-200 mx-2" />
        {showDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="text-gray-500 hover:text-red-600 transition-all duration-200 hover:scale-110"
            title="Delete this item"
          >
            <FaTrash className="text-[14px]" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-x-2 gap-y-1">
        {PakkaInvoiceFields?.length
          ? PakkaInvoiceFields?.map((f, i) => {
              const path = `stockEntries.${rowindex}.${f.name}`;
              const rules = {
                required:
                  f.mandatory || (!itemCodeDetail && f.name === "counterRow")
                    ? `${f.label} is required`
                    : false,
              };

              const colSpan = [
                "description",
                // "category",
                "product",
                // "design",
              ].includes(f.name)
                ? "md:col-span-2"
                : "md:col-span-1";

              const hasErr = !!errors?.stockEntries?.[rowindex]?.[f.name];

              // Counter
              if (f.type === "dropdown" && f.name === "counterRow") {
                return (
                  <div key={f.name} className={colSpan}>
                    <label className="block text-xs font-medium text-gray-700">
                      {f.label}{" "}
                      {(f.mandatory ||
                        (!itemCodeDetail && f.name === "counterRow")) && (
                        <span className="text-red-500">*</span>
                      )}
                    </label>
                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={handleDropDowns({
                            key: "counter",
                            counters: countersFromState,
                            branch: selectedBranch,
                          })}
                          classNamePrefix="react-select"
                          styles={withErrorBorder(selectStyles, hasErr)}
                          menuPlacement="bottom"
                          placeholder="Select..."
                          isClearable
                          isSearchable
                          isDisabled={itemCodeDetail}
                          onChange={(opt) => field.onChange(opt ?? null)}
                          ref={(selectRef) => {
                            if (i === 0 && registerFirstField && selectRef) {
                              registerFirstField(fieldId, {
                                focus: () => selectRef?.focus?.(),
                              });
                            }
                          }}
                        />
                      )}
                    />
                  </div>
                );
              }

              // Item Code
              if (f.type === "dropdown" && f.name === "itemCodeDetail") {
                return (
                  <div key={f.name} className={colSpan}>
                    <label className="block text-xs font-medium text-gray-700 flex justify-between">
                      <span>
                        {f.label}{" "}
                        {f.mandatory && <span className="text-red-500">*</span>}
                      </span>

                      <BsQrCodeScan
                        title={"Scan Qr Code"}
                        className=" md:hidden cursor-pointer"
                        onClick={() => setOpenQr(true)}
                      />
                    </label>

                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={handleDropDowns({
                            key: f.name,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            branches: branchesFromState,
                            category,
                            product,
                            itemCodes: data?.data?.length ? data?.data : [],
                          })}
                          classNamePrefix="react-select"
                          styles={withErrorBorder(selectStyles, hasErr)}
                          menuPlacement="bottom"
                          placeholder={"Select..."}
                          isOptionDisabled={(opt) =>
                            usedItemCodes.has(opt?.value)
                          }
                          onInputChange={(input, meta) => {
                            if (
                              meta.action === "input-change" &&
                              input?.trim()
                            ) {
                              setSearchItemCode(input);
                            }
                          }}
                          onChange={(opt) => {
                            field.onChange(opt ?? null);
                            handleItemCodeSelect(opt);
                          }}
                          isClearable
                          isSearchable
                        />
                      )}
                    />
                  </div>
                );
              }

              // Other Dropdowns
              if (
                f.type === "dropdown" &&
                f.name !== "itemCodeDetail" &&
                f.name !== "counterRow"
              ) {
                return (
                  <div key={f.name} className={colSpan}>
                    <label className="block text-xs font-medium text-gray-700">
                      {f.label}{" "}
                      {f.mandatory && <span className="text-red-500">*</span>}
                    </label>
                    <Controller
                      name={path}
                      control={control}
                      rules={rules}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={handleDropDowns({
                            key: f.name,
                            categories: categoriesFromState,
                            products: productsFromState,
                            designs: designFromState,
                            purities: purityFromState,
                            branches: branchesFromState,
                            category,
                            product,
                            employees: employees?.data,
                          })}
                          classNamePrefix="react-select"
                          styles={withErrorBorder(selectStyles, hasErr)}
                          menuPlacement={f.name == "soldBy" ? "top" : "bottom"}
                          ref={(selectRef) => {
                            if (i === 0 && registerFirstField && selectRef) {
                              registerFirstField(fieldId, {
                                focus: () => selectRef?.focus?.(),
                              });
                            }
                          }}
                          isClearable
                          isSearchable
                          isDisabled={
                            itemCodeDetail &&
                            (f.name === "category" ||
                              f.name === "product" ||
                              f.name === "design" ||
                              f.name === "purity")
                          }
                        />
                      )}
                    />
                  </div>
                );
              }

              // Stones Button
              if (f.type === "button" && f.name === "stones") {
                return (
                  <div key={f.name} className={colSpan}>
                    <label className="block text-xs font-medium text-gray-700">
                      {f.label}
                    </label>

                    {createPortal(
                      <AnimatePresence>
                        {openStoneModal && (
                          <InvoiceStonePopUp
                            key={`stone-modal-${fieldId}`}
                            initialStones={initialStones}
                            setOpenStoneModal={setOpenStoneModal}
                            onSave={(newStones) => {
                              let pcs = 0,
                                amt = 0,
                                wt = 0;

                              for (const s of newStones || []) {
                                const p = Number(s?.stnPopupPcs) || 0;
                                const a = Number(s?.stnPopupAmt) || 0;
                                const w =
                                  s?.stnPopupDeduct === "deduct"
                                    ? s?.unit === "gram"
                                      ? Number(s?.stnPopupWt) || 0
                                      : (Number(s?.stnPopupWt) || 0) / 5
                                    : 0;
                                pcs += p;
                                amt += a;
                                wt += w;
                              }

                              setValue(
                                `stockEntries.${rowindex}.stones`,
                                newStones,
                                {
                                  shouldDirty: true,
                                },
                              );
                              setValue(
                                `stockEntries.${rowindex}.stonePcs`,
                                String(pcs),
                                { shouldDirty: true },
                              );
                              setValue(
                                `stockEntries.${rowindex}.stoneAmount`,
                                twoDecimal(amt),
                                { shouldDirty: true },
                              );
                              setValue(
                                `stockEntries.${rowindex}.stoneWt`,
                                threeDecimal(wt),
                                { shouldDirty: true },
                              );

                              handleCalculations({
                                rowindex,
                                getValues,
                                setValue,
                              });
                            }}
                            onClose={() => {
                              setOpenStoneModal(false);
                              requestAnimationFrame(() =>
                                stoneBtnRef.current?.focus?.(),
                              );
                            }}
                          />
                        )}
                      </AnimatePresence>,
                      document.body,
                    )}

                    <div className="relative w-fit mt-2">
                      <motion.div
                        className="cursor-pointer text-violet-600"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <button
                          ref={stoneBtnRef}
                          type="button"
                          onClick={() => setOpenStoneModal(true)}
                          aria-haspopup="dialog"
                          aria-expanded={openStoneModal}
                        >
                          <GiCutDiamond size={20} />
                        </button>
                      </motion.div>
                    </div>
                  </div>
                );
              }

              // Diamonds Button
              if (f.type === "button" && f.name === "diamonds") {
                return (
                  <div key={f.name} className={colSpan}>
                    <label className="block text-xs font-medium text-gray-700">
                      {f.label}
                    </label>

                    {createPortal(
                      <AnimatePresence>
                        {openDiamondModal && (
                          <InvoiceDiamondPopUp
                            key={`diamond-modal-${fieldId}`}
                            initialDiamonds={initialDiamonds}
                            setOpenDiamondModal={setOpenDiamondModal}
                            onSave={(newDiamonds) => {
                              let pcs = 0,
                                amt = 0,
                                totalCt = 0;

                              for (const d of newDiamonds || []) {
                                const p = Number(d?.diaPopupPcs) || 0;
                                const a = Number(d?.diaPopupAmt) || 0;
                                const ct = Number(d?.diaPopupWt) || 0;
                                pcs += p;
                                amt += a;
                                totalCt += ct;
                              }

                              setValue(
                                `stockEntries.${rowindex}.diamonds`,
                                newDiamonds,
                                { shouldDirty: true },
                              );
                              setValue(
                                `stockEntries.${rowindex}.diamondPcs`,
                                String(pcs),
                                { shouldDirty: true },
                              );
                              setValue(
                                `stockEntries.${rowindex}.diamondAmount`,
                                twoDecimal(amt),
                                { shouldDirty: true },
                              );
                              setValue(
                                `stockEntries.${rowindex}.diamondWt`,
                                threeDecimal(totalCt),
                                { shouldDirty: true },
                              ); // carat

                              handleCalculations({
                                rowindex,
                                getValues,
                                setValue,
                              });
                            }}
                            onClose={() => {
                              setOpenDiamondModal(false);
                              requestAnimationFrame(() =>
                                diamondBtnRef.current?.focus?.(),
                              );
                            }}
                          />
                        )}
                      </AnimatePresence>,
                      document.body,
                    )}

                    <div className="relative w-fit mt-2">
                      <motion.div
                        className="cursor-pointer text-violet-600"
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <button
                          type="button"
                          ref={diamondBtnRef}
                          onClick={() => setOpenDiamondModal(true)}
                        >
                          <GiCutDiamond size={20} />
                        </button>
                      </motion.div>
                    </div>
                  </div>
                );
              }

              // Inputs
              const isFinalAmount = f.name === "totalItemAmount";
              const reg = register(path, {
                ...rules,
                onChange: isFinalAmount
                  ? undefined
                  : () => handleCalculations({ rowindex, getValues, setValue }),
              });

              return (
                <div key={f.name} className={colSpan}>
                  <label className="block text-xs font-medium text-gray-700">
                    {f.label}{" "}
                    {f.name === "itemGstAmount" && (
                      <span className="text-[9px]">(gst or cgst+sgst)</span>
                    )}{" "}
                    {f.mandatory && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type={f.type === "number" ? "number" : "text"}
                    {...reg}
                    onBlur={
                      isFinalAmount
                        ? (e) => {
                            const target = parseFloat(e.target.value);
                            applyManualFinalAmount(rowindex, target);
                          }
                        : reg.onBlur
                    }
                    ref={(el) => {
                      reg.ref(el);
                      if (i === 0 && registerFirstField)
                        registerFirstField(fieldId, el);
                    }}
                    placeholder={f.placeholder || ""}
                    disabled={
                      f?.disabled ||
                      ((f.name === "qty" ||
                        f.name === "totalWt" ||
                        f.name === "packingWt" ||
                        f.name === "grossWt" ||
                        f.name === "stoneWt" ||
                        f.name === "diamondWt" ||
                        f.name === "netWt" || f.name === "pcs" || f.name === "pieces" || f.name === "stonePcs" || f.name === "diamondPcs") &&
                        watch(`stockEntries.${rowindex}.itemCode`)?.value) ||
                      f.name === "itemGstAmount"
                    }
                    inputMode={f.type === "number" ? "decimal" : undefined}
                    step={f.type === "number" ? "any" : undefined}
                    className={inputCls(hasErr)}
                  />
                </div>
              );
            })
          : ""}
      </div>

      <QrScannerModal
        open={openQr}
        onClose={() => setOpenQr(false)}
        onDetected={handleQrDetected}
      />
    </motion.div>
  );
};

export default InvoiceItemForm;
